/**
 * One look for every numeric control, from the DESIGN.md tokens: `bg-input` for
 * the field, `sm` radius on inputs and buttons, the cyan accent for what is
 * active, mono tabular figures so digits line up as they change.
 */
import styled, { css } from "styled-components";
import {
  COLORS,
  FIELD_COLORS,
  PRIMARY_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { BORDER_RADIUS, SPACING } from "../../../constants/design/spacing";

/** Figures a trader reads against each other must not shift as they change. */
export const monoFigures = css`
  font-family: "JetBrains Mono", "SF Mono", "Fira Code", monospace;
  font-feature-settings: "tnum";
`;

/** WCAG 2.5.8: a target small enough to miss is a target that costs money. */
const TAP_TARGET = 32;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
  width: 100%;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.two}px;
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.two};
  min-height: ${SPACING.four}px;
`;

export const MetaLabel = styled.span`
  color: ${TEXT_COLORS.two};
  white-space: nowrap;
`;

/** The balance itself: a button when clicking it fills MAX, else plain text. */
export const MetaValue = styled.button<{ $interactive: boolean }>`
  ${monoFigures};
  background: none;
  border: none;
  padding: 0;
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.one};
  text-align: right;

  ${({ $interactive }) =>
    $interactive
      ? css`
          cursor: pointer;
          text-decoration: underline dotted ${TEXT_COLORS.three};
          text-underline-offset: 3px;

          &:hover:not(:disabled) {
            color: ${PRIMARY_COLORS.one};
            text-decoration-color: ${PRIMARY_COLORS.one};
          }
        `
      : css`
          cursor: default;
        `};

  &:disabled {
    color: ${TEXT_COLORS.three};
    cursor: not-allowed;
  }
`;

export const InputShell = styled.div<{ $invalid?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.one}px;
  padding: 0 ${SPACING.two}px;
  min-height: ${SPACING.five + SPACING.two}px;
  background: ${FIELD_COLORS.one};
  border-radius: ${BORDER_RADIUS.sm}px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? COLORS.negative.one : "transparent")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:focus-within {
    border-color: ${({ $invalid }) =>
      $invalid ? COLORS.negative.one : PRIMARY_COLORS.one};
  }
`;

export const BareInput = styled.input`
  ${monoFigures};
  flex: 1 1 auto;
  min-width: 0;
  background: none;
  border: none;
  outline: none;
  padding: ${SPACING.two}px 0;
  /* 16px keeps iOS from zooming the page when the field takes focus. */
  font-size: 16px;
  color: ${TEXT_COLORS.one};
  text-align: right;

  &::placeholder {
    color: ${TEXT_COLORS.three};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const SlotLabel = styled.span`
  flex-shrink: 0;
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.three};
  white-space: nowrap;
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.one}px;
  flex-wrap: wrap;
`;

export const Chip = styled.button<{ $active?: boolean }>`
  ${monoFigures};
  flex: 1 1 auto;
  min-width: ${TAP_TARGET + SPACING.two}px;
  min-height: ${TAP_TARGET}px;
  padding: 0 ${SPACING.one}px;
  background: ${({ $active }) => ($active ? PRIMARY_COLORS.four : FIELD_COLORS.one)};
  color: ${({ $active }) => ($active ? PRIMARY_COLORS.one : TEXT_COLORS.two)};
  border: 1px solid
    ${({ $active }) => ($active ? PRIMARY_COLORS.one : "transparent")};
  border-radius: ${BORDER_RADIUS.sm}px;
  font-size: ${FONT_SIZE.one};
  cursor: pointer;

  &:hover:not([aria-disabled="true"]) {
    color: ${TEXT_COLORS.one};
  }

  /* aria-disabled, not disabled: an unavailable MAX must stay focusable so it
     can explain itself. */
  &[aria-disabled="true"] {
    color: ${TEXT_COLORS.four};
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.div`
  font-size: ${FONT_SIZE.one};
  color: ${COLORS.negative.one};
  min-height: ${SPACING.three}px;
`;

export const HintText = styled.div`
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.three};
`;
